import { t } from 'i18next';
import type {
    CustomerMatchReasonChildType,
    CustomerMatchReasonField,
    ProductMatchReasonField,
    SalesOrderMatchReasonField,
    SearchMatchReason,
    SearchMatchType,
} from '../../../server/types/search-types.ts';

/** Display text with `<mark>` runs, plus an optional field label. */
export type SearchHighlight = { text: string; prefix?: string };

/** The parts of a search hit that decide what gets highlighted. */
export type SearchHit = { match_type: SearchMatchType; match_reasons: SearchMatchReason[] };

/** The fields with a slot of their own, plus the single leftover match. */
export type SelectedHighlights<Field extends string> = {
    fields: Partial<Record<Field, SearchHighlight>>;
    extra: SearchHighlight | undefined;
};

/** How one entity picks and labels its highlights. */
type HighlightSpec<Field extends string> = {
    /** Matchable fields, highest priority first. Anything else is dropped. */
    order: readonly Field[];
    /** Fields with a slot of their own; the rest compete for `extra`. */
    primary: readonly Field[];
    getPrefix?: (matchReason: SearchMatchReason) => string | undefined;
    /** Wraps the marked text in context the row shows nowhere else. */
    getText?: (matchReason: SearchMatchReason & { field: Field }, text: string) => string;
};

// Field casing varies by index, so names are normalised. First reason for a field wins.
function orderAndFilterMatchReasons<Field extends string>(
    matchReasons: SearchMatchReason[],
    order: readonly Field[],
): (SearchMatchReason & { field: Field })[] {
    const knownFields: readonly string[] = order;
    const isKnown = (field: string): field is Field => knownFields.includes(field);
    const byField = new Map<Field, SearchMatchReason & { field: Field }>();

    for (const reason of matchReasons) {
        const field = reason.field.toUpperCase();

        if (isKnown(field) && !byField.has(field)) {
            byField.set(field, { ...reason, field });
        }
    }

    return order.flatMap((field) => {
        const reason = byField.get(field);

        return reason ? [reason] : [];
    });
}

// A fuzzy hit stays in the results, but its marked run only resembles the query, so it
// must not look like an exact match.
const stripMarks = (text: string): string => text.replaceAll(/<\/?mark>/g, '');

function selectHighlights<Field extends string>(
    { match_type, match_reasons }: SearchHit,
    { order, primary, getPrefix, getText }: HighlightSpec<Field>,
): SelectedHighlights<Field> {
    const highlights = orderAndFilterMatchReasons(match_reasons, order);

    const toHighlight = (reason: SearchMatchReason & { field: Field }): SearchHighlight => {
        const text = match_type === 'fuzzy' ? stripMarks(reason.text) : reason.text;

        return {
            text: getText ? getText(reason, text) : text,
            prefix: getPrefix?.(reason),
        };
    };

    const fields: Partial<Record<Field, SearchHighlight>> = {};

    for (const reason of highlights) {
        if (primary.includes(reason.field)) {
            fields[reason.field] = toHighlight(reason);
        }
    }

    const extra = highlights.find((h) => !primary.includes(h.field));

    return { fields, extra: extra ? toHighlight(extra) : undefined };
}

function getCustomerMatchReasonPrefix(matchReason: SearchMatchReason): string | undefined {
    if (!matchReason.child_type) {
        return;
    }

    const childTypeToTranslationMapper: Record<string, string> = {
        address: t('search__match_label__address', { ns: 'common' }),
        contact: t('search__match_label__contact', { ns: 'common' }),
    } satisfies Record<CustomerMatchReasonChildType, string>;

    return childTypeToTranslationMapper[matchReason.child_type];
}

// A match on a contact's email or phone number says nothing about whose it is, so the
// hydrated contact name leads and the matched value follows in brackets. A name match is
// the one exception: it already shows the name, as the copy carrying the `<mark>` runs,
// so prefixing `child_name` would only repeat it unhighlighted.
function getCustomerMatchReasonText(
    matchReason: SearchMatchReason & { field: CustomerMatchReasonField },
    text: string,
): string {
    const { child_name, field } = matchReason;

    if (!child_name || field === 'CONTACT.NAME') {
        return text;
    }

    return `${child_name} (${text})`;
}

const customerHighlightSpec: HighlightSpec<CustomerMatchReasonField> = {
    order: [
        'ERP_ID',
        'NAME',
        'ADDRESS.CITY',
        'ADDRESS.STREET',
        'CONTACT.NAME',
        'CONTACT.PHONE_NUMBER',
        'CONTACT.MOBILE_NUMBER',
        'CONTACT.EMAIL',
        'ADDRESS.ZIP_CODE',
    ],
    primary: ['ERP_ID', 'NAME', 'ADDRESS.CITY'],
    getPrefix: getCustomerMatchReasonPrefix,
    getText: getCustomerMatchReasonText,
};

export function selectCustomerHighlightsToDisplay(
    hit: SearchHit,
): SelectedHighlights<CustomerMatchReasonField> {
    return selectHighlights(hit, customerHighlightSpec);
}

// The product row has no child entities, so a match's label comes from its field.
function getProductMatchReasonPrefix(matchReason: SearchMatchReason): string | undefined {
    const fieldToTranslationMapper: Record<string, string> = {
        SUPPLIER_NAME: t('search__match_label__supplier', { ns: 'common' }),
        SUPPLIER_ID: t('search__match_label__supplier', { ns: 'common' }),
        PURCHASE_ORDER_NUMBER: t('search__match_label__purchase_order_number', { ns: 'common' }),
        GTIN: t('search__match_label__gtin', { ns: 'common' }),
        EAN: t('search__match_label__ean', { ns: 'common' }),
    } satisfies Partial<Record<ProductMatchReasonField, string>>;

    return fieldToTranslationMapper[matchReason.field];
}

// PRODUCT_DISPLAY_ID stands in for the id the row already shows, so it shares that slot.
const productHighlightSpec: HighlightSpec<ProductMatchReasonField> = {
    order: [
        'PRODUCT_VARIANT_NAME',
        'PRODUCT_VARIANT_ID',
        'PRODUCT_DISPLAY_ID',
        'SUPPLIER_NAME',
        'PURCHASE_ORDER_NUMBER',
        'GTIN',
        'EAN',
        'SUPPLIER_ID',
    ],
    primary: ['PRODUCT_VARIANT_NAME', 'PRODUCT_VARIANT_ID', 'PRODUCT_DISPLAY_ID'],
    getPrefix: getProductMatchReasonPrefix,
};

export function selectProductHighlightsToDisplay(
    hit: SearchHit,
): SelectedHighlights<ProductMatchReasonField> {
    return selectHighlights(hit, productHighlightSpec);
}

// Both order fields have a slot of their own, so there is never a leftover match.
const salesOrderHighlightSpec: HighlightSpec<SalesOrderMatchReasonField> = {
    order: ['ERP_ID', 'CUSTOMER_ID', 'CUSTOMER_NAME'],
    primary: ['ERP_ID', 'CUSTOMER_ID', 'CUSTOMER_NAME'],
};

export function selectSalesOrderHighlightsToDisplay(
    hit: SearchHit,
): SelectedHighlights<SalesOrderMatchReasonField> {
    return selectHighlights(hit, salesOrderHighlightSpec);
}
